import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import intl from 'react-intl-universal';

import { getISOWeek, format, startOfDay, getISOWeekYear } from 'date-fns';
import red from '@mui/material/colors/red';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import IconButton from '@mui/material/IconButton';
import { getBeginAndEndOfWeek } from '../../utils/DateUtil';
import {
  weekBoardDefaultColumnIds,
  extractDeliveries,
  extractQuestions,
  calculatePPC,
  MONDAY,
} from '../../utils/FieldUtil';
import { getLocale } from '../../utils/LocaleUtil';
import { getWeekBoardStructure } from '../../utils/WeekBoardUtil';
import { ANSWERED_ROW } from '../../utils/Constants';

import { canDropMilestoneOnCell, canDropDeliveryOnCell, canDropQuestionOnCell } from '../../utils/DNDUtils';
import {
  DISCIPLINABLE_CELL,
  MILESTONE_CELL,
  HIGH_READABILITY_CELL_WIDTH,
  DEFAULT_CELL_WIDTH,
} from '../../utils/Constants';

import Cell from './Cell';
import AnsweredCardsList from './AnsweredCardsList';
import Tooltip from '@mui/material/Tooltip';

import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';

import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import PPCProgress from '../common/PPCProgressBar';
import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { Card, CardType } from '@/models/card/CardModel';
import { Board } from '@/models/BoardModel';
import { Authority } from '@/models/permission/AuthorityModel.ts';

const useStyles = () => ({
  dot: {
    height: '25px',
    width: '25px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  labelCellRoot: {
    backgroundColor: '#f3f3f3',
    marginTop: '5px',
  },
  labelCellRootWeekEnd: {
    backgroundColor: '#FFEEEE',
  },
  labelCellRootWeekDay: {
    backgroundColor: '#EEFFEE',
  },
  labelCellRootOdd: {
    backgroundColor: '#DEDEDE',
  },
  labelCell: {
    wordWrap: 'break-word',
  },
  milestoneContent: {
    padding: '5px',
    paddingTop: '25px',
  },
  weekHeader: {
    height: '65px',
    display: 'flex',
    alignItems: 'center',
    borderLeft: '1px solid lightgrey',
    justifyContent: 'center',
  },
  weekHeaderHighContrast: {
    height: '65px',
    display: 'flex',
    alignItems: 'center',
    borderLeft: '1px solid lightgrey',
    justifyContent: 'center',
  },
  icon: {
    height: '16px',
    width: '16px',
  },
  iconHighContrast: {
    height: '32px',
    width: '32px',
  },
  weekLabel: {
    fontSize: '14px',
  },
  weekLabelHighContrast: {
    fontSize: '24px',
  },
  week: {
    backgroundColor: 'transparent',
    color: 'black',
  },
  currentWeek: {
    backgroundColor: '#3eb1c8',
  },
  currentWeekHighReadability: {
    backgroundColor: '#000000',
  },
  dayColumn: {
    backgroundColor: 'transparent',
    color: 'black',
  },
  currentWeekDayColumn: {
    backgroundColor: 'transparent',
    color: 'black',
  },
  overdueWeek: {
    backgroundColor: red[300],
  },
  overdue: {
    color: red[300],
  },
  milestoneCell: {
    flexDirection: 'column',
    maxWidth: '200px',
    height: '300px',
    borderRadius: 0,
    backgroundColor: '#EEEEEE',
    display: 'flex',
    justifyContent: 'center',
  },
  rootOdd: {
    backgroundColor: '#DEDEDE',
  },
  rootWeekEnd: {
    backgroundColor: '#FFEEEE',
  },
  rootWeekDay: {
    backgroundColor: '#EEFFEE',
  },
  root: {
    backgroundColor: '#EEEEEE',
  },
  dateLabel: {
    display: 'inline-block',
    height: '10px',
    lineHeight: '1.0rem',
  },
  expandCollapseIcon: {
    cursor: 'pointer',
    width: '24px',
    height: '24px',
    padding: '0px',
  },
  truncated: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '167px',
  },
  cellRoot: {
    padding: '0px 0px 0px 0px',
    textAlign: 'center',
    '&:last-child': {
      paddingRight: 0,
    },
  },
  header: {
    fontSize: '14px',
  },
  headerHighContrast: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  itemHeaderHighContrast: {
    height: '50px',
    cursor: 'pointer',
  },
  itemHeader: {
    height: '10px',
    cursor: 'pointer',
  },
  itemHeaderCollapsed: {
    borderBottom: '1px solid lightgrey',
  },
  selected: {
    backgroundColor: '#EAEAEA',
  },
  progressContainer: {
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dateColumn: { width: '100%', display: 'flex', flexDirection: 'column' },
  dateColumnContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  PPCProgress: { display: 'inline-block', height: '5px' },
  yColumnHeader: {
    paddingLeft: 0,
  },
  rootHeader: {
    marginTop: 0,
    position: 'relative',
  },
});

let locale;

const ANSWERED_LIST_ID = 'answered cards list';

const WeekBoard = ({
  cards,
  onExpandCollapseColumn,
  onUpdateCard,
  onChange,
  milestones,
  deliveries,
  width,
  board,
  viewConfig,
  config,
  onSetScrollPos,
  scrollPos,
  onCardClick,
  roomMembers,
}: {
  cards: Card[];
  onExpandCollapseColumn: (weekColumnId: string) => void;
  onUpdateCard: (
    card: Card,
    rowIndex: number,
    columnIndex: number,
    sourceRowIndex: number,
    sourceColumnIndex: number,
  ) => void;
  onChange: (viewConfig: any) => void;
  milestones: Card[];
  deliveries: Card[];
  width: number;
  board: Board;
  viewConfig: any;
  config: any;
  onSetScrollPos: (scrollPos: any) => void;
  scrollPos: any;
  onCardClick: (card: Card) => void;
  roomMembers: Authority[];
}) => {
  const classes = useStyles();
  const tableWrapperRef = useRef();

  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;

    if (tableWrapper && scrollPos.scrollY && scrollPos.scrollY > 0) {
      (tableWrapper as HTMLElement).scrollTop = scrollPos.scrollY;
    }

    if (tableWrapper && scrollPos.scrollX && scrollPos.scrollX > 0) {
      (tableWrapper as HTMLElement).scrollLeft = scrollPos.scrollX;
    }

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      if (tableWrapper) {
        const scrollTop = (tableWrapper as HTMLElement).scrollTop;
        const scrollLeft = (tableWrapper as HTMLElement).scrollLeft;
        onSetScrollPos({
          pos: { scrollY: scrollTop, scrollX: scrollLeft },
        });
      }
      window.removeEventListener('scroll', handleScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (event) => {
    const lastScrollX = Math.max(0, event.target.scrollLeft);
    if (event?.target?.scrollTop === 0) {
      window.requestAnimationFrame(() => {
        for (let i = 0; i < config.xColumns.length; i++) {
          const row = document.getElementById(`col-${i}`);
          if (row) {
            row.style.left = `${lastScrollX}px`;
          }
        }
      });
    }
  };

  const handleUpdateCard = ({
    card,
    rowIndex,
    columnIndex,
    sourceRowIndex,
    sourceColumnIndex,
  }: {
    card: Card;
    rowIndex: number;
    columnIndex: number;
    sourceRowIndex: number;
    sourceColumnIndex: number;
  }) => {
    onUpdateCard({
      card,
      rowIndex,
      columnIndex,
      sourceRowIndex,
      sourceColumnIndex,
      config,
    });
  };

  const handleClick = (id: string) => {
    const expanded = viewConfig.expandedWeekRows.indexOf(id) > -1;

    if (expanded) {
      onChange(
        viewConfig.set(
          'expandedWeekRows',
          viewConfig.expandedWeekRows.filter((item: string) => item !== id),
        ),
      );
    } else {
      onChange(viewConfig.set('expandedWeekRows', viewConfig.expandedWeekRows.concat(id)));
    }
  };

  const handleExpandCell = ({ key }: { key: string }) => {
    onChange(viewConfig.set('expandedWeekCells', viewConfig.expandedWeekCells.concat(key)));
  };

  const handleCollapseCell = ({ key }: { key: string }) => {
    onChange(
      viewConfig.set(
        'expandedWeekCells',
        viewConfig.expandedWeekCells.filter((item: string) => {
          return item !== key;
        }),
      ),
    );
  };

  const canDropOnCell = ({
    card,
    rowIndex,
    columnIndex,
    sourceRowIndex,
    sourceColumnIndex,
  }: {
    card: Card;
    rowIndex: number;
    columnIndex: number;
    sourceRowIndex: number;
    sourceColumnIndex: number;
  }) => {
    if (card.type === CardType.MILESTONE) {
      return canDropMilestoneOnCell({
        card,
        columnIndex,
        rowIndex,
        sourceRowIndex,
        sourceColumnIndex,
      });
    }
    if (card.type === CardType.DELIVERY) {
      return canDropDeliveryOnCell({
        card,
        board,
        columnIndex,
        rowIndex,
        sourceRowIndex,
        sourceColumnIndex,
        config,
      });
    }
    // if QUESTION
    return canDropQuestionOnCell({
      card,
      board,
      columnIndex,
      rowIndex,
      sourceRowIndex,
      sourceColumnIndex,
      config,
    });
  };

  const getCellClassName = ({
    columnIndex,
    rowIndex,
    config,
  }: {
    columnIndex: number;
    rowIndex: number;
    config: any;
  }) => {
    const isHeaderCell = rowIndex === 0 || rowIndex === config.xColumns.length - 1;

    if (config.yColumns[columnIndex].dayColumn && config.yColumns[columnIndex].weekend) {
      return isHeaderCell ? classes.labelCellRootWeekEnd : classes.rootWeekEnd;
    }

    if (config.yColumns[columnIndex].dayColumn && !config.yColumns[columnIndex].weekend) {
      return isHeaderCell ? classes.labelCellRootWeekDay : classes.rootWeekDay;
    }

    return isHeaderCell ? classes.labelCellRoot : classes.root;
  };

  const getColumnClassName = ({ weekColumnId }: { weekColumnId: string }) => {
    const isDayColumn = weekColumnId.indexOf('-') > -1;
    const weekNumber = getISOWeek(Date.now());
    const year = getISOWeekYear(Date.now(), { weekStartsOn: MONDAY });
    const isCurrentWeek =
      weekColumnId.indexOf(getISOWeek(Date.now()) + '-') > -1 || weekColumnId === `${weekNumber}:${year}`;

    if (isCurrentWeek && isDayColumn) {
      return classes.currentWeekDayColumn;
    }

    if (isCurrentWeek) {
      return viewConfig.highReadability ? classes.currentWeekHighReadability : classes.currentWeek;
    }

    if (isDayColumn) {
      return classes.dayColumn;
    }

    return classes.week;
  };

  const getYHeader = ({ index }: { index: number }) => {
    const weekColumnId = config.yColumns[index].id;
    const week = config.yColumns[index].weekNumber;
    const year = config.yColumns[index].year;
    const handleClick = () => {
      const weekNumber = config.yColumns[index].weekNumber;
      if (weekNumber !== undefined) {
        onExpandCollapseColumn({ weekColumnId: `${week}:${year}` });
      }
    };
    let content;
    if (weekColumnId === weekBoardDefaultColumnIds.NO_DUE_DATE) {
      content = getNoDueDateHeader();
    } else if (weekColumnId === weekBoardDefaultColumnIds.OVERDUE) {
      content = getOverdueHeader();
    } else {
      content = config.yColumns[index].dayColumn
        ? getDayColumnHeader(index, handleClick)
        : getWeekColumnHeader(index, handleClick);
    }
    return content;
  };

  const getWeekColumnHeader = (index, handleClick) => {
    const weekColumnId = config.yColumns[index].id;
    const className = getColumnClassName({ weekColumnId });
    const content = config.yColumns[index].name;
    const { dateFrom, dateTo } = getFromToDates(config.yColumns[index].date, viewConfig);
    // this hack is making lazy calculating PPC and prevents errors on non-updated cards
    // cause there is a case when we have new activeBoard and old questionCards in store at the same time
    // the technical debt to fix roots of it was created DARWIN-16806
    const ppc = viewConfig.showPPC ? getWeekPPC(dateFrom, dateTo) : {};
    return (
      <Box component="div" sx={classes.dateColumn}>
        <Box component="div" sx={classes.dateColumnContainer}>
          <Box component="div" />
          <Avatar
            sx={{
              ...(viewConfig.highReadability ? classes.weekLabelHighContrast : classes.weekLabel),
              ...className,
            }}
          >
            {`${content}`}
          </Avatar>
          <Box component="div">
            <Tooltip title={intl.get('board.views.week.tooltip.expand_week_days')}>
              <IconButton sx={classes.expandCollapseIcon} onClick={handleClick}>
                <KeyboardArrowRight />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box component="span" sx={classes.progressContainer}>
          {viewConfig.showPPC && !!ppc.total && (
            <PPCProgress
              title={intl.get('board.toolbar.ppc.weekly')}
              viewConfig={viewConfig}
              sx={classes.PPCProgress}
              style={{ width: '130px' }}
              ppc={ppc}
              dateFrom={format(dateFrom, 'eee, dd MMM yyyy', {
                locale: getLocale(locale),
              })}
              dateTo={format(dateTo, 'eee, dd MMM yyyy', {
                locale: getLocale(locale),
              })}
            />
          )}
        </Box>
      </Box>
    );
  };

  const getDayColumnHeader = (index, handleClick) => {
    const weekColumnId = config.yColumns[index].id;
    const className = getColumnClassName({ weekColumnId });

    return (
      <Box component="div" sx={classes.dateColumn}>
        <Box component="div" sx={classes.dateColumnContainer}>
          <Box component="div" />
          <Avatar
            sx={{
              ...(viewConfig.highReadability ? classes.weekLabelHighContrast : classes.weekLabel),
              ...className,
            }}
          >
            {`${config.yColumns[index].weekNumber}`}
          </Avatar>
          <Box component="div">
            <Tooltip title={intl.get('board.views.week.tooltip.collapse_week_days')}>
              <IconButton sx={classes.expandCollapseIcon} onClick={handleClick}>
                <KeyboardArrowLeft />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box component="span" sx={classes.dateLabel}>{`${format(config.yColumns[index].date, 'eee, dd MMM yyyy', {
          locale: getLocale(locale),
        })}`}</Box>
      </Box>
    );
  };

  const getOverdueHeader = () => {
    return (
      <Typography
        variant="body2"
        sx={{
          ...(viewConfig.highReadability ? classes.weekLabelHighContrast : classes.weekLabel),
          ...(!viewConfig.highReadability && classes.overdue),
        }}
      >
        {intl.get('board.views.week.columns.overdue')}
      </Typography>
    );
  };

  const getNoDueDateHeader = () => {
    return (
      <Typography
        sx={{
          ...(viewConfig.highReadability ? classes.weekLabelHighContrast : classes.weekLabel),
        }}
        variant="body2"
      >
        {intl.get('board.views.week.columns.no_due_date')}
      </Typography>
    );
  };

  const getWeekPPC = (dateFrom, dateTo) => {
    const dels = extractDeliveries(deliveries, board.delivery_config);
    const questions = extractQuestions(cards, board.field_config);
    return calculatePPC(dateFrom, dateTo, questions.concat(dels));
  };

  const getFromToDates = (endOfWeek) => {
    const week = getBeginAndEndOfWeek(endOfWeek);
    const dateFrom = startOfDay(viewConfig.dateFrom) < week.begin ? week.begin : startOfDay(viewConfig.dateFrom);
    const dateTo = startOfDay(viewConfig.dateTo) > week.end ? week.end : startOfDay(viewConfig.dateTo);
    return { dateFrom, dateTo };
  };

  const renderAnswered = (answered) => {
    return (
      <div>
        <ListItem
          sx={{
            ...(viewConfig.highReadability ? classes.itemHeaderHighContrast : classes.itemHeader),
            ...(viewConfig.expandedWeekRows.indexOf(ANSWERED_LIST_ID) > -1 && classes.selected),
          }}
          style={{
            width: 500,
            marginTop: 50,
            background: 'none',
          }}
          onClick={() => handleClick(ANSWERED_LIST_ID)}
        >
          {viewConfig.expandedWeekRows.indexOf(ANSWERED_LIST_ID) > -1 ? (
            <ExpandLess sx={viewConfig.highReadability ? classes.iconHighContrast : classes.icon} />
          ) : (
            <ExpandMore sx={viewConfig.highReadability ? classes.iconHighContrast : classes.icon} />
          )}
          <ListItemText
            sx={{
              ...classes.rootHeader,
              paddingLeft: '10px',
            }}
            primaryTypographyProps={{
              sx: {
                ...(viewConfig.highReadability ? classes.headerHighContrast : classes.header),
                ...classes.yColumnHeader,
              },
            }}
            primary={intl.get('board.views.week.columns.answered', {
              amount: answered.length,
            })}
          />
        </ListItem>
        <Collapse in={viewConfig.expandedWeekRows.indexOf(ANSWERED_LIST_ID) > -1} timeout="auto" unmountOnExit>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <AnsweredCardsList
              data={answered}
              onCardClick={onCardClick}
              board={board}
              viewConfig={viewConfig}
              roomMembers={roomMembers}
            />
          </div>
        </Collapse>
      </div>
    );
  };

  const renderTable = (showMilestones, weekBoardStructure) => {
    return config.xColumns.map((row, rowIndex) => {
      if (rowIndex === 0 && !showMilestones) {
        return '';
      }
      return (
        <div key={rowIndex} style={{ marginTop: 5 }}>
          <ListItem
            sx={{
              ...(viewConfig.highReadability ? classes.itemHeaderHighContrast : classes.itemHeader),
              ...(viewConfig.expandedWeekRows.indexOf(row.id) === -1 && classes.itemHeaderCollapsed),
              width:
                config.yColumns.length *
                (viewConfig.highReadability ? HIGH_READABILITY_CELL_WIDTH : DEFAULT_CELL_WIDTH),
            }}
            onClick={() => handleClick(row.id)}
          >
            {viewConfig.expandedWeekRows.indexOf(row.id) > -1 ? (
              <ExpandLess
                sx={{
                  ...(viewConfig.highReadability ? classes.iconHighContrast : classes.icon),
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  ...(viewConfig.highReadability ? classes.iconHighContrast : classes.icon),
                }}
              />
            )}
            <ListItemText
              id={`col-${rowIndex}`}
              sx={{
                ...classes.rootHeader,
                paddingLeft: '10px',
              }}
              primaryTypographyProps={{
                sx: {
                  ...(viewConfig.highReadability ? classes.headerHighContrast : classes.header),
                  // ...classes.yColumnHeader,
                },
              }}
              inset
              primary={row.name || intl.get('board.views.week.columns.milestones')}
            />
          </ListItem>
          <Collapse in={viewConfig.expandedWeekRows.indexOf(row.id) > -1} timeout="auto" unmountOnExit>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {config.yColumns.map((column, columnIndex) =>
                getCell(rowIndex, columnIndex, weekBoardStructure[rowIndex][columnIndex].data),
              )}
            </div>
          </Collapse>
        </div>
      );
    });
  };

  const getCell = (rowIndex, columnIndex, cellData) => {
    return (
      <Cell
        canDrop={canDropOnCell}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        key={`${rowIndex}-${columnIndex}`}
        expanded={viewConfig.expandedWeekCells.indexOf(`${rowIndex}-${columnIndex}`) > -1}
        onExpand={handleExpandCell}
        onCollapse={handleCollapseCell}
        data={cellData}
        type={rowIndex === 0 ? MILESTONE_CELL : DISCIPLINABLE_CELL}
        onCardClick={onCardClick}
        onUpdateCard={handleUpdateCard}
        board={board}
        viewConfig={viewConfig}
        roomMembers={roomMembers}
      />
    );
  };

  locale = intl.getInitOptions().currentLocale;

  const showMilestones = milestones && milestones.length > 0;
  const weekBoardStructure = getWeekBoardStructure(board, viewConfig, deliveries, cards, milestones, config);

  return (
    <div
      style={{
        width,
        overflowX: 'scroll',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          width:
            config.yColumns.length * (viewConfig.highReadability ? HIGH_READABILITY_CELL_WIDTH : DEFAULT_CELL_WIDTH),
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              style={{
                boxShadow:
                  '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
              }}
            >
              {config.yColumns.map((column, index) => (
                <TableCell
                  key={`${index}-${column.id}`}
                  sx={{
                    ...classes.cellRoot,
                    width: viewConfig.highReadability ? HIGH_READABILITY_CELL_WIDTH : DEFAULT_CELL_WIDTH,
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      ...(viewConfig.highReadability ? classes.weekHeaderHighContrast : classes.weekHeader),
                      ...getCellClassName({
                        columnIndex: index,
                        rowIndex: 0,
                        config,
                      }),
                    }}
                  >
                    {getYHeader({ index })}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
        <div
          ref={tableWrapperRef}
          style={{
            height: window.innerHeight - 258,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {renderTable(showMilestones, weekBoardStructure)}
          {renderAnswered(weekBoardStructure[ANSWERED_ROW])}
        </div>
      </div>
    </div>
  );
};

export default WeekBoard;
